import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  styled,
  alpha,
  useTheme,
  TextField,
  Button,
  InputAdornment,
  Collapse,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import NotificationSnackbar from "./NotificationBar";

// Import the new ViewPayrollApprovers component
import ViewPayrollApprovers from "./ViewPayrollApprovers"; 

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "600px",
  overflowX: "auto",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "12px",
  "&::-webkit-scrollbar": { height: "8px", width: "8px" },
  "&::-webkit-scrollbar-track": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.primary.main, 0.25),
    borderRadius: "4px",
    "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.4) },
  },
}));

function EntityPayrollApprovers() {
  const theme = useTheme();
  const PRIMARY_COLOR = "#0a2e52";
  const SECONDARY_COLOR = "#1565C0";
  const HEADER_BG_COLOR = "#f4f7fa";
  
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const handleSnackbarClose = () => setSnackbarOpen(false);

  // --- NEW STATE: To track the selected sector ID for viewing approvers ---
  const [selectedSectorId, setSelectedSectorId] = useState(null);

  useEffect(() => {
    const fetchEntities = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch("/api/support/entities");
        if (!response.ok) {
          throw new Error("Failed to fetch entities");
        }
        const data = await response.json();
        setEntities(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching entities:", err);
        setError("Could not load entities. Please try again later.");
        setEntities([]);
      } finally {
        setLoading(false);
      }
    };
    fetchEntities();
  }, []);

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleViewApprovers = (entity, sector) => {
    // Set the selected sector ID to switch views
    setSelectedSectorId(sector.entitySectorId);
  };
  
  const handleBackToList = () => {
    // Clear the selected sector ID to return to the table
    setSelectedSectorId(null);
  };

  const filteredEntities = entities.filter((entity) =>
    entity.name?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // --- CONDITIONAL RENDERING ---
  // If a sector is selected, show the ViewPayrollApprovers component instead of the list
  if (selectedSectorId) {
    return (
      <ViewPayrollApprovers 
        entitySectorId={selectedSectorId} 
        onBack={handleBackToList} 
      />
    );
  }

  // Otherwise, render the main Entity list
  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "16px",
        p: { xs: 2, sm: 3 },
        mb: 3,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
        minHeight: "75vh",
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          mb: 4,
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY_COLOR }}>
          Entity Payroll Approvers
        </Typography>
      </Box>

      {/* SEARCH AND DATA VIEW */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          border: `1px solid ${alpha(PRIMARY_COLOR, 0.1)}`,
          bgcolor: alpha(PRIMARY_COLOR, 0.015),
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 1, color: PRIMARY_COLOR, fontWeight: 600 }}
        >
          Search Entities
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
          Find an institution to view their sector-specific payroll approvers.
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
            mb: 3,
          }}
        >
          <TextField
            placeholder="Search by Entity Name..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {searchTerm && (
                    <IconButton
                      aria-label="clear"
                      size="small"
                      onClick={handleClearSearch}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
            sx={{
              flexGrow: 1,
              maxWidth: 500,
              bgcolor: "white",
              borderRadius: 1,
            }}
          />
        </Box>

        {/* LOADING STATE */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress sx={{ color: PRIMARY_COLOR }} />
          </Box>
        )}

        {/* ERROR STATE */}
        {error && (
          <Typography
            color="error"
            sx={{ fontSize: "0.95rem", fontWeight: 500 }}
          >
            {error}
          </Typography>
        )}

        {/* DATA TABLE */}
        <Collapse in={!loading && !error} timeout="auto">
          {filteredEntities && filteredEntities.length > 0 ? (
            <Box>
              <Typography
                sx={{ fontWeight: 600, color: PRIMARY_COLOR, mb: 1.5 }}
              >
                Found {filteredEntities.length} Entity
                {filteredEntities.length > 1 ? "s" : ""}
              </Typography>
              <ResponsiveTableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="medium">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          backgroundColor: HEADER_BG_COLOR,
                          width: "25%",
                        }}
                      >
                        Entity Name
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          backgroundColor: HEADER_BG_COLOR,
                          width: "30%",
                        }}
                      >
                        Contact Info
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          backgroundColor: HEADER_BG_COLOR,
                          width: "25%",
                        }}
                      >
                        Sector
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontWeight: 700,
                          backgroundColor: HEADER_BG_COLOR,
                          width: "20%",
                        }}
                      >
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredEntities.map((entity) => {
                      const sectors = entity.entitySectors || [];
                      const rowCount = Math.max(sectors.length, 1);

                      return (
                        <React.Fragment key={entity.id}>
                          {sectors.length > 0 ? (
                            sectors.map((sector, index) => (
                              <TableRow key={sector.entitySectorId} hover>
                                {/* Only render Entity Name and Contact for the FIRST row, spanning the rest */}
                                {index === 0 && (
                                  <>
                                    <TableCell
                                      rowSpan={rowCount}
                                      sx={{
                                        fontWeight: 700,
                                        color: PRIMARY_COLOR,
                                        verticalAlign: "top",
                                      }}
                                    >
                                      {entity.name?.trim() || "-"}
                                    </TableCell>
                                    <TableCell
                                      rowSpan={rowCount}
                                      sx={{
                                        verticalAlign: "top",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 1,
                                        }}
                                      >
                                        {entity.institutionEmail && (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                            }}
                                          >
                                            <EmailOutlinedIcon
                                              sx={{
                                                fontSize: 18,
                                                color: "text.secondary",
                                              }}
                                            />
                                            <Typography variant="body2">
                                              {entity.institutionEmail}
                                            </Typography>
                                          </Box>
                                        )}
                                        {entity.institutionPhone && (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                            }}
                                          >
                                            <LocalPhoneOutlinedIcon
                                              sx={{
                                                fontSize: 18,
                                                color: "text.secondary",
                                              }}
                                            />
                                            <Typography variant="body2">
                                              {entity.institutionPhone}
                                            </Typography>
                                          </Box>
                                        )}
                                        {entity.websiteUrl && (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                            }}
                                          >
                                            <LanguageOutlinedIcon
                                              sx={{
                                                fontSize: 18,
                                                color: "text.secondary",
                                              }}
                                            />
                                            <Typography variant="body2">
                                              {entity.websiteUrl}
                                            </Typography>
                                          </Box>
                                        )}
                                        {entity.poBox && (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                            }}
                                          >
                                            <LocationOnOutlinedIcon
                                              sx={{
                                                fontSize: 18,
                                                color: "text.secondary",
                                              }}
                                            />
                                            <Typography variant="body2">
                                              {entity.poBox}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    </TableCell>
                                  </>
                                )}
                                {/* Rendered for EVERY sector */}
                                <TableCell sx={{ fontWeight: 500 }}>
                                  {sector.sectorName}
                                </TableCell>
                                <TableCell align="center">
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<VisibilityIcon />}
                                    onClick={() =>
                                      handleViewApprovers(entity, sector)
                                    }
                                    sx={{
                                      textTransform: "none",
                                      borderRadius: "6px",
                                      fontWeight: 600,
                                      color: SECONDARY_COLOR,
                                      borderColor: SECONDARY_COLOR,
                                      "&:hover": {
                                        bgcolor: alpha(SECONDARY_COLOR, 0.1),
                                      },
                                    }}
                                  >
                                    View Approvers
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            /* Fallback if an entity has 0 sectors */
                            <TableRow hover>
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: PRIMARY_COLOR,
                                  verticalAlign: "top",
                                }}
                              >
                                {entity.name?.trim() || "-"}
                              </TableCell>
                              <TableCell sx={{ verticalAlign: "top" }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                  }}
                                >
                                  {entity.institutionEmail && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <EmailOutlinedIcon
                                        sx={{
                                          fontSize: 18,
                                          color: "text.secondary",
                                        }}
                                      />
                                      <Typography variant="body2">
                                        {entity.institutionEmail}
                                      </Typography>
                                    </Box>
                                  )}
                                  {entity.institutionPhone && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <LocalPhoneOutlinedIcon
                                        sx={{
                                          fontSize: 18,
                                          color: "text.secondary",
                                        }}
                                      />
                                      <Typography variant="body2">
                                        {entity.institutionPhone}
                                      </Typography>
                                    </Box>
                                  )}
                                  {entity.websiteUrl && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <LanguageOutlinedIcon
                                        sx={{
                                          fontSize: 18,
                                          color: "text.secondary",
                                        }}
                                      />
                                      <Typography variant="body2">
                                        {entity.websiteUrl}
                                      </Typography>
                                    </Box>
                                  )}
                                  {entity.poBox && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <LocationOnOutlinedIcon
                                        sx={{
                                          fontSize: 18,
                                          color: "text.secondary",
                                        }}
                                      />
                                      <Typography variant="body2">
                                        {entity.poBox}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  No Sectors
                                </Typography>
                              </TableCell>
                              <TableCell align="center">-</TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </ResponsiveTableContainer>
            </Box>
          ) : (
            !loading && (
              <Typography sx={{ color: "text.secondary" }}>
                No entities found matching "{searchTerm}".
              </Typography>
            )
          )}
        </Collapse>
      </Paper>

      {/* REUSABLE SNACKBAR NOTIFICATION */}
      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
}

export default EntityPayrollApprovers;